export type AudioBarChromeDetail = {
  visible: boolean;
  pinned: boolean;
  extraChromeBottomPx: number;
};

export const AUDIO_BAR_STATE_EVENT = 'app:audioBarState';

type AudioBarChromeEntry = {
  pinned: boolean;
  extraChromeBottomPx: number;
};

const entries = new Map<symbol, AudioBarChromeEntry>();

function computeDetail(): AudioBarChromeDetail {
  let pinned = false;
  let extraChromeBottomPx = 0;
  for (const entry of entries.values()) {
    if (entry.pinned) {
      pinned = true;
      extraChromeBottomPx = Math.max(extraChromeBottomPx, entry.extraChromeBottomPx);
    }
  }
  return {
    visible: entries.size > 0,
    pinned,
    extraChromeBottomPx,
  };
}

function dispatchAudioBarState(): void {
  window.dispatchEvent(
    new CustomEvent<AudioBarChromeDetail>(AUDIO_BAR_STATE_EVENT, {
      detail: computeDetail(),
    })
  );
}

/** Register or update one text+audio block's viewport-pinned chrome contribution. */
export function syncAudioBarChromeEntry(
  id: symbol,
  pinned: boolean,
  extraChromeBottomPx: number
): void {
  entries.set(id, { pinned, extraChromeBottomPx });
  dispatchAudioBarState();
}

/** Remove a text+audio block from the global chrome aggregate. */
export function clearAudioBarChromeEntry(id: symbol): void {
  if (!entries.delete(id)) return;
  dispatchAudioBarState();
}

/** Bottom offset for mobile FABs when the text+audio bar is viewport-pinned (matches expand-tools button). */
export function mobilePinnedAudioChromeBottom(
  bannerCollapsed: boolean,
  extraChromeBottomPx: number
): string {
  const extra = extraChromeBottomPx > 0 ? `${extraChromeBottomPx}px` : '0px';
  const base = bannerCollapsed ? '6.1rem' : '9.35rem';
  return `calc(${base} + ${extra})`;
}

/** Chat FAB when audio bar is pinned — higher when bottom banner is expanded (no expand-tools button). */
export function mobileChatFabPinnedBottom(
  bannerCollapsed: boolean,
  extraChromeBottomPx: number
): string {
  if (bannerCollapsed) {
    return mobilePinnedAudioChromeBottom(true, extraChromeBottomPx);
  }
  const extra = extraChromeBottomPx > 0 ? `${extraChromeBottomPx}px` : '0px';
  // Banner rail (4.25rem) + audio bar (5.5rem) + small gap above the bar
  return `calc(10.25rem + ${extra})`;
}

export type LearnerCornerFabAnchor = {
  left?: string;
  right?: string;
  bottom?: string;
};

/**
 * Shared corner-FAB placement. Index 0 sits just above the bottom banner;
 * each higher index stacks another button above it.
 */
export function learnerCornerFabAnchor(opts: {
  isMobile: boolean;
  isRtl: boolean;
  bannerCollapsed: boolean;
  audioBarPinned: boolean;
  audioBarExtraChromePx: number;
  /** 0 = lowest button (AI chat). Higher indexes stack upward. */
  stackIndex?: number;
}): LearnerCornerFabAnchor {
  const stackLift = (opts.stackIndex ?? 0) * 3.6;
  const sideKey = opts.isRtl ? 'left' : 'right';
  const sideValue = '1rem';

  let bottom: string;
  bottom = opts.audioBarPinned
    ? mobileChatFabPinnedBottom(opts.bannerCollapsed, opts.audioBarExtraChromePx)
    : opts.bannerCollapsed
      ? '1rem'
      : '4.75rem';

  if (stackLift > 0) {
    bottom = `calc(${bottom} + ${stackLift}rem)`;
  }

  return { [sideKey]: sideValue, bottom };
}
